import { omit } from "lodash";

import { formatPhysician } from "utils/physicians.utils";

import { formatValidQueryParamUrlCommaSeparated } from "../utils/common";
import { emptySplitApi } from "./empty-split-api";
import {
  CheckNPIResponse,
  NPPESSearchParams,
  NPPESSearchResponse,
  Physician,
  PhysicianCSVQuery,
  PhysicianFilter,
  PhysicianMergeDetails,
  PhysiciansResponse
} from "./types/physician.type";
import { CSVReportResponse } from "./types/reports.type";

const physiciansApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPhysicians: builder.query<PhysiciansResponse, PhysicianFilter>({
      query: (filter) =>
        `referring-physicians/?is_active=${filter?.is_active ?? ""}${
          filter?.search ? `&search=${encodeURIComponent(filter.search)}` : ""
        }${filter?.patient ? `&patient=${filter.patient}` : ""}${
          filter?.query ? `&query=${encodeURIComponent(filter.query)}` : ""
        }&limit=${
          filter?.limit || 300
        }&offset=${filter?.offset || 0}&ordering=last_name,first_name,pk`,
      providesTags: ["Physicians"],
      transformResponse: (response: PhysiciansResponse) =>
        response.map((referringPhysician) =>
          formatPhysician(referringPhysician)
        )
    }),
    getPhysiciansCount: builder.query<number, boolean>({
      query: (is_active) =>
        `referring-physicians/count/?is_active=${is_active}`,
      providesTags: ["Physicians"]
    }),
    getPhysician: builder.query<Physician, number>({
      query: (referringPhysicianId) =>
        `referring-physicians/${referringPhysicianId}/`,
      providesTags: ["Physicians"],
      transformResponse: (referringPhysician: Physician) =>
        formatPhysician(referringPhysician)
    }),
    addPhysician: builder.mutation<Physician, Physician>({
      query: (referringPhysician) => ({
        url: "referring-physicians/",
        method: "POST",
        body: referringPhysician
      }),
      invalidatesTags: ["Physicians"]
    }),
    editPhysician: builder.mutation<Physician, Physician>({
      query: (referringPhysician) => ({
        url: `referring-physicians/${referringPhysician.id}/`,
        method: "PATCH",
        body: omit(referringPhysician, "id")
      }),
      invalidatesTags: ["Physicians"]
    }),
    downloadPhysiciansCSV: builder.query<CSVReportResponse, PhysicianCSVQuery>({
      query: (filters) => ({
        url: `referring-physicians/?${formatValidQueryParamUrlCommaSeparated(
          filters
        )}`,
        headers: { Accept: "custom-csv/json" }
      })
    }),
    npiSearch: builder.query<NPPESSearchResponse, NPPESSearchParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.number) searchParams.set("number", params.number);
        if (params.first_name)
          searchParams.set("first_name", params.first_name);
        if (params.last_name) searchParams.set("last_name", params.last_name);
        if (params.city) searchParams.set("city", params.city);
        if (params.state) searchParams.set("state", params.state);
        if (params.limit) searchParams.set("limit", String(params.limit));
        if (params.skip) searchParams.set("skip", String(params.skip));
        return `referring-physicians/npi-search/?${searchParams.toString()}`;
      }
    }),
    checkNpi: builder.query<CheckNPIResponse, string>({
      query: (npi) => `referring-physicians/check-npi/${npi}/`
    }),
    getMergePhysicianDetails: builder.query<
      [PhysicianMergeDetails, PhysicianMergeDetails],
      { source_id: number; dest_id: number }
    >({
      query: (params) => ({
        url: "referring-physicians/merge-details/",
        params
      })
    }),
    mergePhysicians: builder.mutation<
      Physician,
      { source_id: number; dest_id: number }
    >({
      query: (body) => ({
        url: "referring-physicians/merge/",
        method: "PATCH",
        body
      }),
      invalidatesTags: ["Physicians"]
    })
  })
});

export const {
  useGetPhysiciansQuery,
  useGetPhysicianQuery,
  useLazyGetPhysicianQuery,
  useAddPhysicianMutation,
  useEditPhysicianMutation,
  useLazyDownloadPhysiciansCSVQuery,
  useGetPhysiciansCountQuery,
  useLazyNpiSearchQuery,
  useLazyCheckNpiQuery,
  useGetMergePhysicianDetailsQuery,
  useMergePhysiciansMutation
} = physiciansApi;
